import { useCallback, useEffect, useRef, useState } from "react";

import { StillProcessingIndicator } from "./StillProcessingIndicator";

import {
  getSignedPdfUrlResult,
  type SignedPdfUrlResult,
} from "../../../../../services/files";

const POLL_INTERVAL_MS = 3_000;
const MAX_POLL_MS = 60_000;

interface StillProcessingFallbackProps {
  url: string;
  fileName: string;
  onReady: (signedUrl: string) => void;
  onGiveUp: () => void;
}

export function StillProcessingFallback({
  url,
  fileName,
  onReady,
  onGiveUp,
}: StillProcessingFallbackProps) {
  const [retrying, setRetrying] = useState(false);
  const startedAtRef = useRef(Date.now());
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tryResolve = useCallback(async (): Promise<SignedPdfUrlResult> => {
    return getSignedPdfUrlResult(url);
  }, [url]);

  const handleResult = useCallback(
    (result: SignedPdfUrlResult) => {
      if (result.status === "ok") {
        onReady(result.url);
        return;
      }
      if (
        result.status === "not_ready" &&
        Date.now() - startedAtRef.current < MAX_POLL_MS
      ) {
        pollTimerRef.current = setTimeout(async () => {
          const next = await tryResolve();
          handleResult(next);
        }, POLL_INTERVAL_MS);
        return;
      }
      onGiveUp();
    },
    [onGiveUp, onReady, tryResolve],
  );

  useEffect(() => {
    startedAtRef.current = Date.now();
    void tryResolve().then(handleResult);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [handleResult, tryResolve]);

  const handleRetryClick = async () => {
    setRetrying(true);
    startedAtRef.current = Date.now();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    const result = await tryResolve();
    setRetrying(false);
    handleResult(result);
  };

  return (
    <StillProcessingIndicator
      fileName={fileName}
      retrying={retrying}
      onRetry={() => void handleRetryClick()}
    />
  );
}
